import { useState } from 'react';
import { AlertTriangle, Check, CircleHelp, ShieldAlert, X } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useResolveAgentDecision } from '../../api/useAdminOps';
import CollapsibleMarkdown from './CollapsibleMarkdown';
import DecisionContextView from './DecisionContextView';
import { timeAgo, timeUntil } from './opsUi';

// One required-human-decision card: what the agent asked, why, and inline
// resolution. Every pending decision (any kind) offers the agent's option
// chips plus a free-form answer/note — approve/reject carry it as a note,
// "Send answer" resolves as 'answered'; either way the follow-up run sees it.
// Approvals with a proposedAction show exactly what the server will execute.

const KIND_META = {
  approval: { icon: ShieldAlert, label: 'Approval', chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  question: { icon: CircleHelp, label: 'Question', chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  escalation: { icon: AlertTriangle, label: 'Escalation', chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
} as const;

const STATUS_CHIPS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  answered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  expired: 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
};

export default function DecisionCard({
  decision,
  context,
}: {
  decision: AdminAPI.AgentDecision;
  /** Resolved people and tables behind the proposed action, when it has one. */
  context?: AdminAPI.DecisionContext;
}) {
  const resolve = useResolveAgentDecision();
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const meta = KIND_META[decision.kind];
  const Icon = meta.icon;
  const isPending = decision.status === 'pending';
  const overdue = isPending && decision.dueBy && new Date(decision.dueBy) < new Date();

  const options = Array.isArray(decision.options)
    ? (decision.options as unknown[]).map(String).filter(Boolean)
    : [];
  // Chip + free text combine into one answer; either alone works too.
  const answer = selected
    ? text.trim()
      ? `${selected} — ${text.trim()}`
      : selected
    : text.trim();

  // Approve/reject carry the input along as a note; "Send answer" resolves as
  // 'answered' with it — the follow-up run reads both.
  const handle = (resolution: 'approved' | 'rejected' | 'answered') => {
    resolve.mutate({
      decisionId: decision.id,
      resolution,
      ...(resolution === 'answered' ? { answer } : answer ? { note: answer } : {}),
    });
  };

  return (
    <div
      className={`rounded-xl border p-4 bg-white dark:bg-neutral-900 ${
        overdue
          ? 'border-red-400 dark:border-red-600'
          : 'border-neutral-200 dark:border-neutral-700'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-dark-muted" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.chip}`}>
                {meta.label}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_CHIPS[decision.status]}`}>
                {decision.status}
              </span>
              <span className="text-xs text-dark-muted">
                {decision.neighborhoodName ??
                  (decision.neighborhoodId === null ? 'Event-wide' : null)}
              </span>
              <span className="text-xs text-dark-muted">· {timeAgo(decision.createdAt)}</span>
              {isPending && decision.dueBy && (
                <span className={`text-xs font-medium ${overdue ? 'text-red-600' : 'text-amber-600'}`}>
                  {overdue ? 'OVERDUE' : `due ${timeUntil(decision.dueBy)}`}
                </span>
              )}
            </div>
            <h4 className="font-semibold text-sm mt-1.5">{decision.title}</h4>
            <CollapsibleMarkdown className="text-dark-muted mt-1 break-words" collapseAt={340}>
              {decision.body}
            </CollapsibleMarkdown>
            {context && <DecisionContextView context={context} />}
            {decision.proposedAction != null && (
              <details className="mt-2">
                <summary className="text-xs font-medium text-dark-muted cursor-pointer">
                  Exact payload executed on approval
                </summary>
                <pre className="mt-1 text-[11px] bg-neutral-50 dark:bg-neutral-800 rounded-lg p-2 overflow-x-auto">
                  {JSON.stringify(decision.proposedAction, null, 2)}
                </pre>
              </details>
            )}
            {!isPending && decision.resolution != null && (
              <div className="mt-2 text-xs text-dark-muted">
                Resolved {decision.resolvedByName ? `by ${decision.resolvedByName} ` : ''}
                {timeAgo(decision.resolvedAt)}
                {(decision.resolution as { answer?: string })?.answer && (
                  <span> — answer: {(decision.resolution as { answer?: string }).answer}</span>
                )}
                {(decision.resolution as { note?: string })?.note && (
                  <span> — note: {(decision.resolution as { note?: string }).note}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isPending && (
        <div className="mt-3 space-y-2">
          {options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSelected(selected === opt ? null : opt)}
                  className={`px-2.5 py-1 rounded-full text-xs border ${
                    selected === opt
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                      : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                decision.kind === 'approval'
                  ? 'Answer or note (sent with any button)…'
                  : 'Your answer…'
              }
              className="flex-1 min-w-[180px] px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-sm"
            />
            {decision.kind === 'approval' && (
              <>
                <button
                  onClick={() => handle('approved')}
                  disabled={resolve.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Check className="w-3.5 h-3.5" />
                  Approve
                </button>
                <button
                  onClick={() => handle('rejected')}
                  disabled={resolve.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                  Reject
                </button>
              </>
            )}
            {decision.kind === 'escalation' && !answer && (
              <button
                onClick={() => handle('approved')}
                disabled={resolve.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                Mark resolved
              </button>
            )}
            {(decision.kind !== 'approval' || answer) && (
              <button
                onClick={() => handle('answered')}
                disabled={!answer || resolve.isPending}
                className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {decision.kind === 'approval' ? 'Answer instead' : 'Send answer'}
              </button>
            )}
            {decision.kind !== 'approval' && (
              <button
                onClick={() => handle('rejected')}
                disabled={resolve.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {resolve.isError && (
        <p className="mt-2 text-xs text-red-600">
          Failed to resolve: {(resolve.error as Error)?.message ?? 'unknown error'}
        </p>
      )}
    </div>
  );
}
